// src/components/health/export/ExportWizard.tsx
// Export Wizard Component for Health Data

import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, subMonths } from 'date-fns';
import {
  Download,
  FileText,
  FileSpreadsheet,
  FileJson,
  File,
  Calendar,
  Filter,
  CheckCircle,
  Loader2,
  X,
  ArrowLeft,
  ArrowRight,
} from 'lucide-react';
import type { 
  ExportConfiguration, 
  ExportResult, 
  ExportProgress,
  ReportFormat,
} from '@/features/health/types/healthTypes/reportTypes';

// ============================================================================
// Types
// ============================================================================

interface ExportWizardProps {
  childIds: string[];
  children: Array<{ id: string; name: string }>;
  logTypes: Array<{ id: string; name: string }>;
  onExport: (config: ExportConfiguration) => Promise<ExportResult>;
  onClose?: () => void;
  onProgress?: (progress: ExportProgress) => void;
}

type Step = 'format' | 'date-range' | 'options' | 'exporting' | 'complete';

// ============================================================================
// Format Options
// ============================================================================

const formatOptions: Array<{
  id: ReportFormat;
  name: string;
  description: string;
  icon: React.FC<{ className?: string }>;
  extension: string;
}> = [
  {
    id: 'csv',
    name: 'CSV',
    description: 'Spreadsheet-compatible format',
    icon: FileSpreadsheet,
    extension: '.csv',
  },
  {
    id: 'json',
    name: 'JSON',
    description: 'Machine-readable data format',
    icon: FileJson,
    extension: '.json',
  },
  {
    id: 'pdf',
    name: 'PDF',
    description: 'Print-ready document',
    icon: FileText,
    extension: '.pdf',
  },
  {
    id: 'excel',
    name: 'Excel',
    description: 'Microsoft Excel workbook',
    icon: File,
    extension: '.xlsx',
  },
];

// ============================================================================
// Component
// ============================================================================

export const ExportWizard: React.FC<ExportWizardProps> = ({
  childIds,
  children,
  logTypes,
  onExport,
  onClose,
  onProgress,
}) => {
  // State
  const [currentStep, setCurrentStep] = useState<Step>('format');
  const [selectedFormat, setSelectedFormat] = useState<ReportFormat>('csv');
  const [dateRange, setDateRange] = useState({
    start: subMonths(new Date(), 1),
    end: new Date(),
  });
  const [selectedChildIds, setSelectedChildIds] = useState<string[]>(childIds);
  const [selectedLogTypes, setSelectedLogTypes] = useState<string[]>(logTypes.map((t) => t.id));
  const [includeMedications, setIncludeMedications] = useState(true);
  const [includeNotes, setIncludeNotes] = useState(true);
  const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month' | 'type'>('day');
  const [sortBy, setSortBy] = useState<'date' | 'type' | 'severity'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [progress, setProgress] = useState<ExportProgress | null>(null);
  const [result, setResult] = useState<ExportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Handle export
  const handleExport = useCallback(async () => {
    setCurrentStep('exporting');
    setError(null);

    const config: ExportConfiguration = {
      format: selectedFormat,
      dateRange: {
        startDate: dateRange.start,
        endDate: dateRange.end,
      },
      childIds: selectedChildIds,
      logTypes: selectedLogTypes,
      includeMedications,
      includeNotes,
      includeAttachments: false,
      groupBy,
      sortBy,
      sortOrder,
    };

    try {
      const exportResult = await onExport(config);
      setResult(exportResult);
      setCurrentStep('complete');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
      setCurrentStep('options');
    }
  }, [selectedFormat, dateRange, selectedChildIds, selectedLogTypes, includeMedications, includeNotes, groupBy, sortBy, sortOrder, onExport]);

  // Update progress
  const updateProgress = useCallback((p: ExportProgress) => {
    setProgress(p);
    onProgress?.(p);
  }, [onProgress]);

  // Navigation
  const steps: Step[] = ['format', 'date-range', 'options'];
  const currentStepIndex = steps.indexOf(currentStep);

  const goNext = () => {
    if (currentStep === 'options') {
      handleExport();
    } else if (currentStepIndex < steps.length - 1) {
      setCurrentStep(steps[currentStepIndex + 1]);
    }
  };

  const goBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStep(steps[currentStepIndex - 1]);
    } else if (onClose) {
      onClose();
    }
  };

  // Download result
  const handleDownload = useCallback(() => {
    if (result?.downloadUrl) {
      window.open(result.downloadUrl, '_blank');
    } else if (result?.blob) {
      const url = URL.createObjectURL(result.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  }, [result]);

  // ============================================================================
  // Render Steps
  // ============================================================================

  const renderStep = () => {
    switch (currentStep) {
      case 'format':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-display font-semibold text-navy mb-1">
                Choose Export Format
              </h3>
              <p className="text-sm text-warm-gray">
                Select how you want to export your health data
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {formatOptions.map((option) => {
                const IconComponent = option.icon;
                const isSelected = selectedFormat === option.id;

                return (
                  <button
                    key={option.id}
                    onClick={() => setSelectedFormat(option.id)}
                    className={`
                      p-4 rounded-xl border-2 text-left
                      transition-all duration-200
                      ${isSelected
                        ? 'border-sage bg-sage/5'
                        : 'border-cream-dark hover:border-sage-light'
                      }
                    `}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`
                        w-10 h-10 rounded-lg flex items-center justify-center
                        ${isSelected ? 'bg-sage/20' : 'bg-cream'}
                      `}>
                        <IconComponent className={`
                          w-5 h-5
                          ${isSelected ? 'text-sage-dark' : 'text-warm-gray'}
                        `} />
                      </div>
                      <div>
                        <h4 className="font-medium text-navy">
                          {option.name}
                          <span className="text-xs text-warm-gray ml-1">
                            {option.extension}
                          </span>
                        </h4>
                        <p className="text-sm text-warm-gray">
                          {option.description}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );

      case 'date-range':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-display font-semibold text-navy mb-1">
                Select Date Range
              </h3>
              <p className="text-sm text-warm-gray">
                Choose the time period to export
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-navy mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={format(dateRange.start, 'yyyy-MM-dd')}
                  onChange={(e) => setDateRange((prev) => ({
                    ...prev,
                    start: new Date(e.target.value),
                  }))}
                  className="w-full px-4 py-3 rounded-xl border border-cream-dark bg-white
                    focus:ring-2 focus:ring-sage/20 focus:border-sage outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-navy mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={format(dateRange.end, 'yyyy-MM-dd')}
                  onChange={(e) => setDateRange((prev) => ({
                    ...prev,
                    end: new Date(e.target.value),
                  }))}
                  className="w-full px-4 py-3 rounded-xl border border-cream-dark bg-white
                    focus:ring-2 focus:ring-sage/20 focus:border-sage outline-none"
                />
              </div>
            </div>

            {/* Child Selection */}
            {children.length > 1 && (
              <div>
                <label className="block text-sm font-medium text-navy mb-2">
                  Select Children
                </label>
                <div className="flex flex-wrap gap-2">
                  {children.map((child) => {
                    const isSelected = selectedChildIds.includes(child.id);
                    return (
                      <button
                        key={child.id}
                        onClick={() => {
                          setSelectedChildIds((prev) =>
                            isSelected
                              ? prev.filter((id) => id !== child.id)
                              : [...prev, child.id]
                          );
                        }}
                        className={`
                          px-4 py-2 rounded-lg text-sm font-medium
                          transition-all duration-200
                          ${isSelected
                            ? 'bg-sage text-white'
                            : 'bg-cream text-navy hover:bg-cream-dark'
                          }
                        `}
                      >
                        {child.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Log Type Selection */}
            <div>
              <label className="block text-sm font-medium text-navy mb-2">
                Log Types
              </label>
              <div className="flex flex-wrap gap-2">
                {logTypes.map((type) => {
                  const isSelected = selectedLogTypes.includes(type.id);
                  return (
                    <button
                      key={type.id}
                      onClick={() => {
                        setSelectedLogTypes((prev) =>
                          isSelected
                            ? prev.filter((id) => id !== type.id)
                            : [...prev, type.id]
                        );
                      }}
                      className={`
                        px-3 py-1.5 rounded-lg text-sm font-medium
                        transition-all duration-200
                        ${isSelected
                          ? 'bg-sage text-white'
                          : 'bg-cream text-navy hover:bg-cream-dark'
                        }
                      `}
                    >
                      {type.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        );

      case 'options':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-display font-semibold text-navy mb-1">
                Export Options
              </h3>
              <p className="text-sm text-warm-gray">
                Customize your export settings
              </p>
            </div>

            {/* Include Options */}
            <div className="space-y-3">
              <label className="flex items-center justify-between p-4 rounded-xl border border-cream-dark bg-white cursor-pointer hover:border-sage-light">
                <span className="font-medium text-navy">Include Medications</span>
                <input
                  type="checkbox"
                  checked={includeMedications}
                  onChange={(e) => setIncludeMedications(e.target.checked)}
                  className="w-5 h-5 rounded text-sage focus:ring-sage"
                />
              </label>

              <label className="flex items-center justify-between p-4 rounded-xl border border-cream-dark bg-white cursor-pointer hover:border-sage-light">
                <span className="font-medium text-navy">Include Notes</span>
                <input
                  type="checkbox"
                  checked={includeNotes}
                  onChange={(e) => setIncludeNotes(e.target.checked)}
                  className="w-5 h-5 rounded text-sage focus:ring-sage"
                />
              </label>
            </div>

            {/* Grouping */}
            <div>
              <label className="block text-sm font-medium text-navy mb-2">
                Group By
              </label>
              <select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value as typeof groupBy)}
                className="w-full px-4 py-3 rounded-xl border border-cream-dark bg-white
                  focus:ring-2 focus:ring-sage/20 focus:border-sage outline-none"
              >
                <option value="day">Day</option>
                <option value="week">Week</option>
                <option value="month">Month</option>
                <option value="type">Log Type</option>
              </select>
            </div>

            {/* Sorting */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-navy mb-2">
                  Sort By
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                  className="w-full px-4 py-3 rounded-xl border border-cream-dark bg-white
                    focus:ring-2 focus:ring-sage/20 focus:border-sage outline-none"
                >
                  <option value="date">Date</option>
                  <option value="type">Type</option>
                  <option value="severity">Severity</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-navy mb-2">
                  Order
                </label>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value as typeof sortOrder)}
                  className="w-full px-4 py-3 rounded-xl border border-cream-dark bg-white
                    focus:ring-2 focus:ring-sage/20 focus:border-sage outline-none"
                >
                  <option value="desc">Newest First</option>
                  <option value="asc">Oldest First</option>
                </select>
              </div>
            </div>

            {error && (
              <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                {error}
              </div>
            )}
          </div>
        );

      case 'exporting':
        return (
          <div className="flex flex-col items-center justify-center py-16">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            >
              <Loader2 className="w-12 h-12 text-sage" />
            </motion.div>
            <h3 className="text-lg font-display font-semibold text-navy mt-6 mb-2">
              Exporting Data
            </h3>
            {progress && (
              <>
                <p className="text-warm-gray text-center">
                  {progress.message}
                </p>
                <div className="w-64 h-2 bg-cream rounded-full mt-4 overflow-hidden">
                  <motion.div
                    className="h-full bg-sage rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress.progress}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
              </>
            )}
          </div>
        );

      case 'complete':
        return (
          <div className="flex flex-col items-center justify-center py-12">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15 }}
            >
              <CheckCircle className="w-16 h-16 text-success" />
            </motion.div>
            <h3 className="text-xl font-display font-semibold text-navy mt-6 mb-2">
              Export Complete!
            </h3>
            {result && (
              <>
                <p className="text-warm-gray text-center mb-2">
                  {result.filename}
                </p>
                <p className="text-sm text-warm-gray mb-8">
                  {(result.size / 1024).toFixed(1)} KB
                </p>
                <button
                  onClick={handleDownload}
                  className="flex items-center gap-2 px-6 py-3 rounded-xl
                    bg-sage text-white font-medium
                    hover:bg-sage-dark transition-colors shadow-soft"
                >
                  <Download className="w-5 h-5" />
                  Download File
                </button>
              </>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // ============================================================================
  // Main Render
  // ============================================================================

  return (
    <div className="bg-white rounded-2xl shadow-medium overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-cream-dark bg-cream/30 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-sage/20 flex items-center justify-center">
            <Download className="w-5 h-5 text-sage-dark" />
          </div>
          <div>
            <h2 className="font-display font-semibold text-navy">
              Export Health Data
            </h2>
            <p className="text-sm text-warm-gray">
              Download your health logs and records
            </p>
          </div>
        </div>

        {currentStep !== 'exporting' && currentStep !== 'complete' && (
          <div className="flex items-center gap-2">
            {steps.map((step, index) => (
              <div
                key={step}
                className={`
                  w-2 h-2 rounded-full transition-colors
                  ${index <= currentStepIndex ? 'bg-sage' : 'bg-cream-dark'}
                `}
              />
            ))}
          </div>
        )}

        {onClose && (
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-warm-gray hover:text-navy hover:bg-cream transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Content */}
      <div className="p-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer */}
      {currentStep !== 'exporting' && currentStep !== 'complete' && (
        <div className="px-6 py-4 border-t border-cream-dark bg-cream/30 flex items-center justify-between">
          <button
            onClick={goBack}
            className="flex items-center gap-2 px-4 py-2 rounded-lg
              text-warm-gray hover:text-navy transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {currentStepIndex === 0 ? 'Cancel' : 'Back'}
          </button>

          <button
            onClick={goNext}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl
              bg-sage text-white font-medium
              hover:bg-sage-dark transition-colors shadow-soft"
          >
            {currentStep === 'options' ? (
              <>
                Export
                <Download className="w-4 h-4" />
              </>
            ) : (
              <>
                Continue
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      )}

      {currentStep === 'complete' && (
        <div className="px-6 py-4 border-t border-cream-dark bg-cream/30 flex items-center justify-center">
          <button
            onClick={() => {
              setCurrentStep('format');
              setResult(null);
            }}
            className="text-sage-dark hover:text-sage transition-colors"
          >
            Export More Data
          </button>
        </div>
      )}
    </div>
  );
};

export default ExportWizard;

